import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Container, WIDGET_REGISTRY } from "../widgets";
import { v4 as uuid } from 'uuid';
import { deserializeWidget, serializeWidget } from "../utils/serializer";
import { nextSiblingOrder } from "../utils/nextSiblingOrder";
import { mergeSnapshotWidgets } from "../utils/mergeSnapshotWidgets";
import { LiveReportCrudContext } from "./LiveReportCrudContext";
import { EditorContext } from "./EditorContextValue";

// Re-export so existing `import { EditorContext } from "./EditorContext"`
// keeps working. New code should prefer importing from EditorContextValue
// to avoid pulling the widget registry into module-load.
export { EditorContext };

/** @typedef {import("../widgets").BaseWidget} BaseWidget */

/**
 * @typedef {object} EditorContextValue
 * @property {string} reportId The ID of the report being edited (used for Firebase syncing)
 * @property {BaseWidget[]} widgets List of all widgets to render
 * @property {(widgets: BaseWidget[]) => void} setWidgets Function to update the list of widgets (triggers re-render)
 * @property {(widget: BaseWidget, parentId: string|null, targetIndex: number) => void} addWidget Function to add a new widget to the canvas
 * @property {(containerId: string, type: 'row' | 'column' | 'sibling') => void} addContainerElements Function to add a new container element to a specified parent container
 * @property {number} controlsFlashToken Counter used to retrigger controls flash animation on edit/select
 * @property {string|null} activeWidgetId Currently active widget ID
 * @property {(id: string|null) => void} setActiveWidgetId Function to set active widget
 * @property {string|null} hoveredWidgetId Currently hovered widget ID (e.g. from tree view)
 * @property {(id: string|null) => void} setHoveredWidgetId Function to set hovered widget
 * @property {(widget: BaseWidget, dropTargetId: string|null, targetIndex: number) => void} onDrop Function to handle dropping a widget into a container (updates parent of dropped widget)
 * @property {(WidgetClass: new (...args: any[]) => BaseWidget, editable?: boolean) => BaseWidget} instantiateWidget Creates a widget based on a type
 * @property {(payload: { droppedId: string, type: string, parentId: string|null, targetIndex: number }) => void} handleWidgetDrop Handles dropping an existing widget or creating a new one from drag payload data
 * @property {(id: string|null) => BaseWidget|null} getWidgetById Get a widget by its ID
 * @property {(tab: string|null) => void} setActiveTab Function to set the active tab in the control panel
 * @property {string|null} activeTab Currently active tab in the control panel 
 * @property {(id: string) => void} deleteWidget Function to delete a widget by its ID
 * @property {(id: string) => void} duplicateWidget Duplicates a widget (and its descendants) and inserts the copy directly after the source.
 * @property {() => void} undo Function to undo the last action
 * @property {() => void} redo Function to redo the last undone action
 * @property {number} historyIndex Current index in the undo/redo history stack (re-renders when it moves)
 * @property {boolean} canUndo Whether there is an earlier history point to go back to
 * @property {boolean} canRedo Whether there is a later history point to go forward to
 * @property {(widgets: BaseWidget[]) => void} saveHistoryState Function to save the current state of widgets for undo/redo
 * @property {(widgets?: BaseWidget[]) => void} save Function to save to Firebase - when no items are passed, saves the current state.
 * @property {() => void} scheduleSave Debounced auto-save — call after a control edit so changes persist without needing to deselect the widget.
 */

/**
 * Dedupe by ID, drop orphans and retire empty extra root containers.
 * @param {BaseWidget[]} newWidgets
 * @returns {BaseWidget[]}
 */
const normalizeWidgets = (newWidgets) => {

    /* Remove duplicates from newWidgets */
    newWidgets = newWidgets.filter((widget, index, self) =>
        index === self.findIndex((w) => w.id === widget.id)
    );

    /* Remove orphaned widgets without valid parent ID */
    newWidgets = newWidgets.filter(widget => {
        if (widget.parent === null) return true; // Root widgets are fine
        if (newWidgets.find(w => w.id === widget.parent)) return true; // Has valid parent in newWidgets
        if (widget.deleted) return false;
        return false; // Orphaned widget
    });

    // If there are multiple root containers (parent == null), 
    // Only keep the root container that actuall has widgets (children.length > 0) 
    // And set the empty root container to 'deleted'. 
    const rootContainers = newWidgets.filter(w => w.parent === null);

    if (rootContainers.length > 1) {
        rootContainers.forEach(root => {
            const hasChildren = newWidgets.some(w => w.parent === root.id && !w.deleted);

            if (!hasChildren) {
                root.deleted = true; // Mark empty root containers as deleted
            }
        });
    }

    return newWidgets;
};

/* Undo steps kept per editor session — one per control change or structural action. */
const MAX_HISTORY_POINTS = 50;

/**
 * Whether two serialized history points hold the same report state. docid is
 * ignored: a new widget gets it on its first save without anything changing.
 * @param {Array<{ docid?: string|null }>} a
 * @param {Array<{ docid?: string|null }>} b
 * @returns {boolean}
 */
const sameHistoryPoint = (a, b) => {
    const strip = (point) => JSON.stringify(point.map(({ docid, ...rest }) => rest));
    return strip(a) === strip(b);
};

// EditorContext now lives in ./EditorContextValue (split so consumers can
// import the bare context without pulling in the widget registry).

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const EditorContextProvider = ({ 
    children 
}) => {

    const liveReportCrudContext = useContext(LiveReportCrudContext);

    if (!liveReportCrudContext) {
        throw new Error('LiveReportCrudContext not found. EditorContextProvider must be used within a LiveReportCrudContextProvider.');
    }

    const {
        reportId,
        report,
        saveWidgets
    } = liveReportCrudContext

    const [widgets, _setWidgets] = useState(/** @type {BaseWidget[]} */ ([]));

    /** @type {EditorContextValue['setWidgets']} */
    const setWidgets = useCallback((incoming) => {
        _setWidgets(current => normalizeWidgets(
            /* A snapshot replaces every widget object — keep the local ones
               that still carry an unsaved edit or delete.
               AI-CONTEXT: See docs/pitfalls.md#state-snapshot-replaces-unsaved-widget */
            mergeSnapshotWidgets(current, incoming)
        ));

        /* Note: should never call 'save' here.
           Firebase onSnapshot calls setWidgets() - would cause infinite loop. */
    }, []);

    const [activeWidgetId, _setActiveWidgetId] = useState(/** @type {string|null} */ (null));
    const [controlsFlashToken, setControlsFlashToken] = useState(0);
    const [hoveredWidgetId, setHoveredWidgetId] = useState(/** @type {string|null} */ (null));;
    const [activeTab, setActiveTab] = useState(/** @type {string|null} */ ('structure'));

    // Maps widgetId → currently-viewed nested-item ID for carousel widgets
    // (Activity / Effect / Research). Transient: not persisted. The canvas
    // Renderer writes when the user navigates; NestedItemControl in the
    // sidebar reads to auto-open the matching accordion.
    const [activeNestedItemIds, setActiveNestedItemIds] = useState(/** @type {Object<string, string>} */ ({}));
    const setActiveNestedItemId = useCallback((widgetId, itemId) => {
        if (!widgetId) return;
        setActiveNestedItemIds(prev => {
            if (prev[widgetId] === itemId) return prev;
            return { ...prev, [widgetId]: itemId };
        });
    }, []);

    /* Pending debounced auto-save timer (see scheduleSave). */
    const autoSaveTimer = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));

    /* Latest widget list, for callbacks that must not re-arm on every render
       (scheduleSave's history point, applyHistoryStep's removals). */
    const widgetsRef = useRef(widgets);
    widgetsRef.current = widgets;

    /* Undo / redo feature. One history point per change: a control edit
       (scheduleSave) or a structural action (save). The ref holds the
       position for synchronous reads; the state mirrors it so the toolbar's
       counter and buttons re-render when it moves. */
    const historyIndex = useRef(0);
    const [historyPosition, setHistoryPosition] = useState({ index: 0, length: 0 });

    /** @type {React.MutableRefObject<ReturnType<typeof serializeWidget>[][]>} */
    const historyStateRef = useRef([]);
    const historyState = historyStateRef.current;

    const syncHistoryPosition = useCallback(() => {
        setHistoryPosition({ index: historyIndex.current, length: historyState.length });
    }, [historyState]);

    /** @type {EditorContextValue['saveHistoryState']} */
    const saveHistoryState = useCallback((widgets) => {

        const serialized = widgets.map((w) => serializeWidget(reportId, w, report?.IsTemplate ?? false));

        /* A save that changed nothing (deselecting, a debounce after the
           control already recorded its point) must not add a step, or one
           change takes two undos. docid is left out of the comparison: a new
           widget gets its docid on its first save, which is no change. */
        const current = historyState[historyIndex.current];
        if (current && sameHistoryPoint(current, serialized)) {
            return;
        }

        if (historyIndex.current < historyState.length - 1) {
            historyState.splice(historyIndex.current + 1);
        }

        historyState.push(serialized);

        while (historyState.length > MAX_HISTORY_POINTS) {
            historyState.shift();
        }

        historyIndex.current = historyState.length - 1;
        syncHistoryPosition();
    }, [historyState, reportId, report, syncHistoryPosition]);

    const saveHistoryStateRef = useRef(saveHistoryState);
    saveHistoryStateRef.current = saveHistoryState;

    /** @param {-1 | 1} step */
    const applyHistoryStep = useCallback((step) => {
        if (!report) {
            return;
        }

        const nextIndex = historyIndex.current + step;

        if (nextIndex < 0 || nextIndex >= historyState.length) return;

        historyIndex.current = nextIndex;
        syncHistoryPosition();

        /** @type {ReturnType<typeof serializeWidget>[]} */
        const state = historyState[historyIndex.current];

        /* Editable, like the snapshot's widgets: these go into editor state. */
        const reconstructedWidgets = state
            .map((w) => deserializeWidget(w, true))
            .filter(w => !w.deleted);

        /* Deserialized widgets come back clean (isDirty: false), but a history
           step must overwrite the stored docs — mark them all dirty so the
           dirty-filtered saveWidgets writes the full state. */
        reconstructedWidgets.forEach(w => {
            w.isDirty = true;
        });

        /* A widget that exists now but not at the target point (added after
           it, or deleted before a redo) must be deleted in storage too —
           writing only the point's own widgets left an undone addition in
           Firestore, and it came back on reload. Never-written widgets have
           no document to delete. */
        const targetIds = new Set(reconstructedWidgets.map(w => w.id));
        const removedWidgets = widgetsRef.current.filter(w =>
            !targetIds.has(w.id) && !w.deleted && w.docid
        );
        removedWidgets.forEach(w => {
            w.deleted = true;
            w.isDirty = true;
        });

        /* The history step is the last word. A pending auto-save would write
           the edit it undoes back over it, and a snapshot keeps the dirty local
           objects that still carry that edit — so cancel the one and replace
           the other. Removed widgets stay in state as deleted so a snapshot
           that predates this write can't bring them back.
           AI-CONTEXT: See docs/pitfalls.md#state-snapshot-replaces-unsaved-widget */
        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current);
            autoSaveTimer.current = null;
        }
        _setWidgets(normalizeWidgets([...reconstructedWidgets, ...removedWidgets]));

        saveWidgets(report, [...reconstructedWidgets, ...removedWidgets]); // Stores Firebase docs

    }, [report, saveWidgets, historyState, syncHistoryPosition]);

    /** @type {EditorContextValue['undo']} */
    const undo = useCallback(() => {
        applyHistoryStep(-1);
    }, [applyHistoryStep]);

    /** @type {EditorContextValue['redo']} */
    const redo = useCallback(() => {
        applyHistoryStep(1);
    }, [applyHistoryStep]);

    /* Store initial state in history on load */
    useEffect(() => {
        if (reportId && widgets.length > 0 && historyState.length === 0) {
            saveHistoryState(widgets); 
        }
    }, [reportId, saveHistoryState, widgets, historyState.length]);

    /**
     * Saves the current widgets to Firestore via LiveReportCrudContext
     * @param {BaseWidget[]} [newWidgets]
     */
    const save = useCallback((newWidgets) => {
        if (!report) {
            return;
        }

        /* An explicit save supersedes any pending debounced auto-save. */
        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current);
            autoSaveTimer.current = null;
        }

        newWidgets = newWidgets || widgets; // Use old state if new state not provided (e.g. for undo/redo)

        saveHistoryState(newWidgets);       // The state being saved is the history point (skipped if unchanged)
        saveWidgets(report, newWidgets);    // Stores Firebase docs -- will also call setWidgets() via snapshot update
    }, [report, saveWidgets, saveHistoryState, widgets]);

    /* Always points at the latest `save` so the debounced timer fires with
       current report/widgets without re-arming on every render. */
    const saveRef = useRef(save);
    saveRef.current = save;

    /**
     * Debounced auto-save. Sidebar control edits (e.g. container height) mutate
     * widgets in memory and re-render, but only deselecting a widget used to
     * persist them — so an edit that wasn't followed by a click-away was lost on
     * reload. Control changes now schedule a save shortly after edits settle.
     * @type {() => void}
     */
    const scheduleSave = useCallback(() => {
        /* Each control change is its own undo step, even when several land
           inside one debounce and share a single save. */
        saveHistoryStateRef.current(widgetsRef.current);

        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current);
        }
        autoSaveTimer.current = setTimeout(() => {
            autoSaveTimer.current = null;
            saveRef.current();
        }, 1000);
    }, []);

    /* On unmount, flush any pending edit before tearing down — otherwise an
       edit followed by navigating away within the debounce window would be
       lost (the exact failure this auto-save fixes, just narrowed to <1s). */
    useEffect(() => () => {
        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current);
            autoSaveTimer.current = null;
            saveRef.current();
        }
    }, []);

    /** 
     * @type {EditorContextValue['getWidgetById']} 
     */
    const getWidgetById = useCallback((id) => {
        return widgets.find(w => w.id === id) ?? null;
    }, [widgets]);

    /**
     * @type {(parentId: string|null) => BaseWidget[]}
     */
    const getWidgetsByParentId = useCallback((parentId) => {
        return widgets.filter(w => w.parent === parentId);
    }, [widgets]);

    /* Bind save() when deselecting a dirty widget. The isDirty flag itself is
       cleared by saveWidgets after a successful write — don't reset it here,
       or a pending change (e.g. restored after a failed save) gets lost. */
    const setActiveWidgetId = useCallback((id) => {

        const currentWidget = getWidgetById(activeWidgetId);

        if (currentWidget && currentWidget.isDirty) {
            save(widgets);
        }

        const newWidget = getWidgetById(id);

        if (!newWidget) {
            /* If no widget found, we're likely deselecting (e.g. clicking on empty canvas space), so reset hovered widget too */
            setActiveTab('structure');
        }
        
        setHoveredWidgetId(null);
        _setActiveWidgetId(id);

        if (id !== null) {
            setControlsFlashToken((token) => token + 1);
        }
    }, [save, widgets, activeWidgetId, getWidgetById]);

    /* Bind ctrl-z / cmd-z for undo and ctrl-shift-z / cmd-shift-z for redo */
    useEffect(() => {
        /** @argument {KeyboardEvent} event */
        const handleKeyDown = (event) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const ctrlKey = isMac ? event.metaKey : event.ctrlKey;

            if (ctrlKey && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                undo();
            } else if ((ctrlKey && event.key === 'Z') || (ctrlKey && event.key === 'z' && event.shiftKey)) {
                event.preventDefault();
                redo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo]);

    /**
     * @type {EditorContextValue['addWidget']}
     */
    const addWidget = (widget, parentId, targetIndex) => {

        const container = getWidgetById(parentId) ??
            widgets.find((w) => w.parent === null); // Default to root container if parentId is empty

        if (!container) {
            alert('No container target found.');
            return;
        }
        
        widget.parent = container.id; // Set parent ID for new widget

        // if (placementId === undefined) {
        //     const siblings = getWidgetsByParentId(container.id);
        //     placementId = siblings.length > 0 ? siblings[siblings.length - 1].id : '';
        //     isAfterTargetElement = true;
        // }

        /* Simply act as if we execute a 'drop' event */
        onDrop(widget, container.id, targetIndex);
        setActiveWidgetId(widget.id); // Set newly added widget as active
    }

    /**
     * @type {EditorContextValue['addContainerElements']}
     */
    const addContainerElements = (containerId, type) => {
        const container = getWidgetById(containerId);

        if (!container || !(container instanceof Container)) {
            return;
        }

        let updatedWidgets = [...widgets];

        /* Add a single sibling */
        if (type === 'sibling') {
            
            const siblings = getWidgetsByParentId(container.id);

            if (siblings.length === 0) {
                type = 'row'; 
            } else {
                const sibling = instantiateWidget(Container, true);
                sibling.parent = container.id;
                // Without an explicit order the serializer writes Order: 0 and
                // render order falls back to Firestore docids — see nextSiblingOrder.js.
                sibling.order = nextSiblingOrder(siblings);

                sibling.setSetting('width', { value: 100, unit: '%' });

                updatedWidgets = [...widgets, sibling];
            }
        }

        /* Convert to a row/column (add 2 elements) */
        if (type === 'row' || type === 'column') {
            const row1 = instantiateWidget(Container, true);
            const row2 = instantiateWidget(Container, true);

            row1.parent = containerId;
            row2.parent = containerId;
            row1.order = nextSiblingOrder(getWidgetsByParentId(containerId));
            row2.order = row1.order + 1;

            row1.setSetting(type === 'row' ? 'width' : 'height', { value: 100, unit: '%' });
            row2.setSetting(type === 'row' ? 'width' : 'height', { value: 100, unit: '%' });

            /* Set container flex direction based on row vs column */
            container.setSetting('flex', {
                ...container.getSetting('flex'),
                flexDirection: (type === 'row') ? 'row' : 'column',
            });
        
            updatedWidgets = [...widgets, row1, row2];
        } 
        
        // setWidgets(updatedWidgets);
        save(updatedWidgets);          // Store updated items -- will also call setWidgets() on snapshot update
    }
    
    /**
     * @param {BaseWidget[]} widgets List of all widgets
     * @param {BaseWidget} widget The widget being moved
     * @param {number} targetIndex The index where the widget should be placed among its siblings
     * @todo can simplify isAfterTargetElement to just add 1 to placementId
     */
    const rebuildOrder = (widgets, widget, targetIndex) => {
        const siblings = getWidgetsByParentId(widget.parent)
            .filter((w) => w.id !== widget.id) // Widget to be re-ordered is excluded
            // A just-deleted sibling stays in state until the snapshot drops it;
            // the canvas no longer renders it, so the drop index the canvas
            // hands us doesn't count it either.
            .filter((w) => !w.deleted);

        const reorderedSiblings = [...siblings]; // Shallow copy
        
        reorderedSiblings.splice(targetIndex, 0, widget);
        reorderedSiblings.forEach((sibling, index) => {
            /* Order changes bypass setSetting — mark dirty here or the
               dirty-filtered save skips the reorder. */
            if (sibling.order !== index) {
                sibling.order = index;
                sibling.isDirty = true;
            }
        });

        const sorted = [...widgets].sort((a, b) => a.order - b.order);

        return sorted;
    }

    /**
     * @type {EditorContextValue['onDrop']}
     */
    const onDrop = (widget, parentId, targetIndex) => {

        if (widget === null) return;

        /* Ignore root ID / dropping into self */
        if (widget.parent === null) {
            return;
        }

        /* If dropped widget is new (e.g. from sidebar), add the new element */
        if (!widgets.find(w => w.id === widget.id)) {
            widgets.push(widget); 
        }

        /* Prevent dropping container onto its own children */
        if (widget.constructor.name === Container.name) {

            // Recurse to check if this container was dropped onto one of it's children. 
            /** @argument {BaseWidget} w The widget to check */
            const isDroppedOnOwnChild = (w) => {
                if (w.id === parentId) {
                    return true; // Dropped onto own child - invalid
                }

                const children = getWidgetsByParentId(w.id);
                
                for (let child of children) {
                    if (isDroppedOnOwnChild(child)) {
                        return true;
                    }
                }

                return false;
            }

            if (isDroppedOnOwnChild(widget)) {
                console.error('Cannot drop a container onto one of its own children.');
                return;
            }
        }

        /* Set parent node. Parent changes bypass setSetting — mark dirty so
           the dirty-filtered save persists the move even when the widget's
           order happens to stay the same. */
        if (widget.parent !== parentId) {
            widget.isDirty = true;
        }
        widget.parent = parentId;

        console.log(widget.parent);

        const rebuilt = rebuildOrder(widgets, widget, targetIndex);

        console.log(rebuilt.find(w => w.id === widget.id));

        // setWidgets(rebuilt);    // Trigger re-render with new parent/order
        save(rebuilt);          // Store updated items -- will also call setWidgets() on snapshot update
    };

    /**
     * @type {EditorContextValue['instantiateWidget']}
     */
    const instantiateWidget = useCallback((WidgetClass, editable = false) => {

        // const WidgetClass = WIDGET_REGISTRY[type];

        const newWidget = new WidgetClass({}, editable);
        newWidget.id = uuid();

        return newWidget;
    }, []);



    /**
     * @type {EditorContextValue['handleWidgetDrop']}
     */
    const handleWidgetDrop = (payload) => {
        const {
            droppedId,
            type,
            parentId,
            targetIndex,
        } = payload;

        const droppedWidget = getWidgetById(droppedId);

        if (!droppedWidget) {
            const WidgetClass = WIDGET_REGISTRY[type];

            if (!WidgetClass) {
                console.error(`Unknown widget type: ${type}`);
                return;
            }

            const newWidget = instantiateWidget(WidgetClass);
            addWidget(newWidget, parentId, targetIndex);
            return;
        }

        onDrop(droppedWidget, parentId, targetIndex);
    };

    /**
     * @type {EditorContextValue['duplicateWidget']}
     */
    const duplicateWidget = (widgetId) => {
        const source = getWidgetById(widgetId);
        if (!source || source.parent === null) return; /* Can't duplicate root */

        const isTemplate = report?.IsTemplate ?? false;

        /* Collect source widget + all descendants in DFS order
           (top-level clone is always first). */
        const subtree = [];
        const visit = (id) => {
            const w = widgets.find(x => x.id === id);
            if (!w || w.deleted) return;
            subtree.push(w);
            widgets
                .filter(c => c.parent === id && !c.deleted)
                .forEach(c => visit(c.id));
        };
        visit(widgetId);

        /* Map each old ID to a fresh one so internal parent links can be
           remapped onto the new copies. */
        const idMap = new Map();
        subtree.forEach(w => idMap.set(w.id, uuid()));

        /* Serialize + clone each. Drop docid so Firestore creates a new
           document for every clone. */
        const clones = subtree.map(w => {
            const serialized = serializeWidget(reportId, w, isTemplate);
            const clone = deserializeWidget({
                ...serialized,
                docid: null,
                ID: idMap.get(w.id),
            }, source.editable);

            /* Top-level clone keeps source's parent; descendants point
               at the cloned ancestor's new ID. Set on the instance so
               we don't have to reproduce the serializer's "null" sentinel. */
            clone.parent = (w.parent !== null && idMap.has(w.parent))
                ? idMap.get(w.parent)
                : w.parent;

            return clone;
        });

        const topClone = clones[0];

        /* Re-number descendants per cloned parent. Subtree order is render
           order, but the source orders themselves can hold ties — copied to
           new docs those would re-roll their docid tiebreak and shuffle. */
        const orderCounters = new Map();
        clones.slice(1).forEach(clone => {
            const order = orderCounters.get(clone.parent) ?? 0;
            orderCounters.set(clone.parent, order + 1);
            clone.order = order;
        });

        /* Re-number live siblings by current render (array) order and slot the
           clone right after the source. Bumping only `order > source.order`
           would skip siblings tied with the source, leaving the clone's slot
           ambiguous. Direct order mutation — mark dirty so it's persisted. */
        const siblings = widgets.filter(w => w.parent === source.parent && !w.deleted);
        const sourceIndex = siblings.indexOf(source);
        siblings.forEach((s, index) => {
            const order = index > sourceIndex ? index + 1 : index;
            if (s.order !== order) {
                s.order = order;
                s.isDirty = true;
            }
        });
        topClone.order = sourceIndex + 1;

        /* Push into the local list so setActiveWidgetId can resolve the clone
           synchronously — Firestore's snapshot will reconcile the array reference. */
        widgets.push(...clones);

        save(widgets);
        setActiveWidgetId(topClone.id);
    };

    /**
     * @type {EditorContextValue['deleteWidget']}
     */
    const deleteWidget = (widgetId) => {
        if (widgetId === null) return;
        
        const widget = getWidgetById(widgetId);
        if (!widget) return;

        if (widget.parent === null) return; /* Can't delete root */

        widget.deleted = true; // Soft delete, will be handled by Firebase sync

        const newWidgets = [...widgets];

        setActiveWidgetId(null);    // Close controls
        // setWidgets(newWidgets);     // Trigger re-render
        save(newWidgets);           // Store/delete documents -- will also call setWidgets() on snapshot update
        setActiveTab('structure'); 
    };

    return(
        <EditorContext.Provider value={{
            save,
            scheduleSave,
            reportId,
            widgets, setWidgets, addWidget, addContainerElements,
            controlsFlashToken,
            activeWidgetId, setActiveWidgetId,
            hoveredWidgetId, setHoveredWidgetId,
            activeTab, setActiveTab,
            activeNestedItemIds, setActiveNestedItemId,
            undo, redo, saveHistoryState,
            historyIndex: historyPosition.index,
            canUndo: historyPosition.index > 0,
            canRedo: historyPosition.index < historyPosition.length - 1,
            deleteWidget,
            duplicateWidget,
            onDrop,
            instantiateWidget,
            handleWidgetDrop,
            getWidgetById
        }}>
            {children}
        </EditorContext.Provider>
    )
}